import { createState } from "@/global/store/zustand_store";
import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";
import { Box, Stack } from "@mui/material";
import { useChooseMissingBucketEventsModal } from "./ChooseMissingBucketEventsModal";
import { throttleTime } from "@/global/utils/utils";
import { useChooseMissingRegularEventsModal } from "./ChooseMissingRegularEventsModal";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { EventType } from "@/global/store/data/types";
import { useChooseMissingPackageEventsModal } from "./ChooseMissingPackageEventsModal";
import { ModalActionButton } from "@/shared/base/components/ModalActionButton/ModalActionButton";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import {
  ContinueWithoutSelectionButton,
  NoInventoryModalMessage,
} from "./force-event-selection-common";
import { CommonEventCard } from "@/shared/common/CommonEventCard";
import { useAppStore } from "@/global/store";
import { selectEvents } from "@/global/store/data/purchaseSeats/events";
import { useFilterPanelsState } from "@/shared/modules/GameFilters/useFilterPanels";
import { dispatchCustomEvent } from "@/global/utils/dispatchCustomEvent";
import { customEvents, filterPanelNames } from "@/global/config/constants";

export interface NoInventoryEventModalResponse {
  action:
    | "confirmedGameSelection"
    | "continue"
    | "backToFindGames"
    | "tryAgain"
    | "close";
  selectedEvents?: EventType[];
}

export const useModalState = createState({
  isOpen: false,
  eventCode: "" as string,
  isInventoryApiTriggered: false as boolean,
  onFinish: (a: NoInventoryEventModalResponse) => {},
});

export const useNoInventoryAlertModal = () => {
  const { isOpen, onFinish, set, reset, ...modalState } = useModalState();
  const execute = (args: typeof modalState) => {
    return new Promise<NoInventoryEventModalResponse>((resolve) => {
      set({ ...args, isOpen: true, onFinish: resolve });
    });
  };
  return { ...modalState, isOpen, onFinish, set, reset, execute };
};

export function NoInventoryAlertModal() {
  const teamConfig = useTeamConfig();
  const { md: mdDown } = useCssMediaQuery("down");
  const { lastChangedFilter } = useFilterPanelsState();
  const modalState = useNoInventoryAlertModal();
  const events = useAppStore(selectEvents);
  const eventToDisplay = events?.find((event) => {
    return event?.eventCode === modalState?.eventCode;
  });

  const chooseBucketEventsModal = useChooseMissingBucketEventsModal();
  const chooseRegularEventsModal = useChooseMissingRegularEventsModal();
  const choosePackageEventsModal = useChooseMissingPackageEventsModal();

  const handleClose = () => {
    modalState.set({ isOpen: false });
    throttleTime(400, () => modalState.reset());
  };

  const onClickChooseAnotherEvent = () => {
    handleClose();

    if (teamConfig?.buckets?.enabled || teamConfig?.packs?.enabled) {
      return chooseBucketEventsModal.set({
        isOpen: true,
        eventCode: modalState.eventCode,
        onFinish: modalState.onFinish,
        isRetryDisabled: false,
      });
    }

    if (teamConfig?.packageConfigs?.isPackageEnabled) {
      return choosePackageEventsModal.set({
        isOpen: true,
        eventCode: modalState.eventCode,
        onFinish: modalState.onFinish,
        isRetryDisabled: false,
      });
    }

    return chooseRegularEventsModal.set({
      isOpen: true,
      eventCode: modalState.eventCode,
      onFinish: modalState.onFinish,
      isRetryDisabled: false,
    });
  };

  const openLastChangedFilterPanel = () => {
    dispatchCustomEvent(customEvents.openGameFilter, {
      name: lastChangedFilter || filterPanelNames.seat_counter,
    });
  };

  const onClickContinue = () => {
    modalState.onFinish({ action: "continue" });
    handleClose();
  };

  const onClickBackToFindGames = () => {
    modalState.onFinish({ action: "backToFindGames" });
    handleClose();
  };

  const onClickTryAgain = () => {
    if (modalState?.isInventoryApiTriggered) openLastChangedFilterPanel();
    modalState.onFinish({ action: "tryAgain" });
    handleClose();
  };

  const onClickAdjustFilters = () => {
    openLastChangedFilterPanel();
    modalState.onFinish({ action: "tryAgain" });
    handleClose();
  };

  return (
    <>
      <NotificationModal
        isOpen={modalState.isOpen}
        hideTitleIcon
        hideCloseIcon={true}
        onClickSecondaryButton={onClickContinue}
        title={
          teamConfig?.languages?.noInventoryAlertModal?.title ||
          "Inventory Unavailable"
        }
        description={
          <Box>
            <Box>
              <NoInventoryModalMessage
                eventCode={modalState?.eventCode}
                isInventoryApiTriggered={modalState?.isInventoryApiTriggered}
                onClickAdjustFilters={onClickAdjustFilters}
              />
            </Box>
            {eventToDisplay && (
              <Box mt={2}>
                <CommonEventCard event={eventToDisplay} />
              </Box>
            )}
          </Box>
        }
        renderFooter={() => {
          return (
            <Stack
              p={2}
              direction={mdDown ? "column" : "row"}
              justifyContent="flex-end"
              spacing={2}
            >
              <ModalActionButton variant="outlined" onClick={onClickTryAgain}>
                {modalState?.isInventoryApiTriggered
                  ? "Change Filters"
                  : "Retry"}
              </ModalActionButton>

              <ModalActionButton
                variant="primary"
                onClick={onClickChooseAnotherEvent}
              >
                Choose Another Event
              </ModalActionButton>

              <ContinueWithoutSelectionButton
                eventCode={modalState?.eventCode}
                onClickContinue={onClickContinue}
                onClickBackToFindGames={onClickBackToFindGames}
                buttonProps={{ variant: "primary" }}
              />
            </Stack>
          );
        }}
      />
    </>
  );
}
